// Paket üyesi varsayılanları gövdesi (DR5; P2: sağ ayrıntı panelinde ya da satır altında). Birim
// (`UnitSelect`: katalog varsayılanı + alternatifler + "Diğer…"; en çok 32 karakter, şablon `{` yok —
// türetilmişte salt okunur), aktif formül (yalnız türetilmiş; katalog varsayılanı + varyantlar),
// değişim sütunu üçlü (katalog / açık / kapalı). Yalnız TASLAĞA yazar; Kaydet şeridi gönderir.

import { useId, useState } from "react";
import { KPI_PACKAGE_UNIT_MAX, type KpiMetric } from "@mahir/shared";
import { KPI_PACKAGES_COPY } from "../../lib/kpi-packages-copy";
import type { PackageDefaults } from "../../lib/kpi-package-draft";
import { UnitSelect } from "./UnitSelect";
import { decisionText, helpText, labelStyle, monoStyle } from "./kpi-form-styles";

const C = KPI_PACKAGES_COPY.defaults;

export type DefaultsMetric = Pick<
  KpiMetric,
  | "id"
  | "label"
  | "defaultUnit"
  | "unitVariants"
  | "isDerived"
  | "defaultFormulas"
  | "showChangeDefault"
>;

/** Paket birimi doğrulaması: boş → katalog; en çok 32 karakter, şablon yok. Hata metni ya da null. */
export function packageUnitError(unit: string | null): string | null {
  if (unit === null) return null;
  if (unit.includes("{")) return KPI_PACKAGES_COPY.errors.KPI_PACKAGE_UNIT_TEMPLATE;
  if (unit.length > KPI_PACKAGE_UNIT_MAX) return C.unitTooLong;
  return null;
}

export interface PackageDefaultsBodyProps {
  metric: DefaultsMetric;
  value: PackageDefaults;
  disabled?: boolean;
  onChange: (patch: Partial<PackageDefaults>) => void;
}

/** Birim · aktif formül · değişim sütunu alanları (açılır pencere ve ayrıntı paneli ortak gövdesi). */
export function PackageDefaultsBody(p: PackageDefaultsBodyProps): JSX.Element {
  const { metric, value, onChange } = p;
  const name = useId();
  const [unitError, setUnitError] = useState<string | null>(null);
  const radio = (
    group: string,
    checked: boolean,
    label: string,
    onPick: () => void,
    extra?: string,
  ): JSX.Element => (
    <label key={`${group}-${label}`} className="flex items-center gap-2" style={{ minHeight: 40 }}>
      <input
        type="radio"
        name={`${name}-${group}`}
        checked={checked}
        disabled={p.disabled}
        onChange={onPick}
        style={{ width: 18, height: 18 }}
      />
      <span style={decisionText}>{label}</span>
      {extra ? <span style={{ ...monoStyle, color: "var(--color-text-2)" }}>{extra}</span> : null}
    </label>
  );

  return (
    <>
      <fieldset style={{ border: 0, padding: 0, margin: "0 0 12px" }}>
        <legend style={labelStyle}>{C.unit}</legend>
        {metric.isDerived ? (
          <p style={{ ...helpText, margin: "4px 0 0" }}>
            {C.unitDerived}
            {metric.defaultUnit ? ` · ${metric.defaultUnit}` : ""}
          </p>
        ) : (
          <>
            <UnitSelect
              ariaLabel={C.unit}
              value={value.unitDefault}
              defaultUnit={metric.defaultUnit}
              variants={metric.unitVariants}
              current={metric.defaultUnit}
              disabled={p.disabled === true}
              onChange={(unit) => {
                const err = packageUnitError(unit);
                setUnitError(err);
                if (!err) onChange({ unitDefault: unit });
              }}
            />
            {unitError ? (
              <span role="alert" style={{ ...helpText, color: "var(--color-error)" }}>
                {unitError}
              </span>
            ) : null}
          </>
        )}
      </fieldset>

      {metric.isDerived && metric.defaultFormulas.length > 0 ? (
        <fieldset style={{ border: 0, padding: 0, margin: "0 0 12px" }}>
          <legend style={labelStyle}>{C.formula}</legend>
          {radio("formula", value.activeFormulaId === null, C.inherit, () =>
            onChange({ activeFormulaId: null }),
          )}
          {metric.defaultFormulas.map((f) =>
            radio(
              "formula",
              value.activeFormulaId === f.id,
              f.label,
              () => onChange({ activeFormulaId: f.id }),
              f.expression,
            ),
          )}
        </fieldset>
      ) : null}

      <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
        <legend style={labelStyle}>{C.showChange}</legend>
        {radio(
          "change",
          value.showChange === null,
          C.inheritWith(metric.showChangeDefault ? C.on : C.off),
          () => onChange({ showChange: null }),
        )}
        {radio("change", value.showChange === true, C.on, () => onChange({ showChange: true }))}
        {radio("change", value.showChange === false, C.off, () => onChange({ showChange: false }))}
      </fieldset>
    </>
  );
}
